import { authStorageService } from './authStorageService'
import { fetchForms } from '../reducers/formsSlice'

const REFETCH_INTERVAL = 5 * 60 * 1000 // 5 minutes

export const pwaService = {
  deferredPrompt: null,
  serviceWorkerRegistration: null,
  updateCallbacks: new Set(),
  lastFetchTimestamp: 0,
  store: null,
  
  initialize(store) {
    this.store = store
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this))
    
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault()
      this.deferredPrompt = e
      if (window) window.deferredPrompt = e
      this.showInstallPromotion()
    })

    window.addEventListener('appinstalled', (e) => {
      this.deferredPrompt = null
      this.hideInstallPromotion()
    })

    this.initializeServiceWorkerUpdates()
  },

  initializeServiceWorkerUpdates() {
    if (import.meta.env.DEV) {
      return
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        this.serviceWorkerRegistration = registration
        this.monitorUpdates(registration)
      }).catch((error) => {
        console.warn('Service worker not ready:', error)
      })

      navigator.serviceWorker.addEventListener('message', (event) => {
        this.handleServiceWorkerMessage(event)
      })
    }
  },

  monitorUpdates(registration) {
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            this.notifyUpdateAvailable(newWorker)
          }
        })
      }
    })
  },

  handleServiceWorkerMessage(event) {
    const { type } = event.data || {}
    
    switch (type) {
      case 'UPDATE_AVAILABLE':
        this.notifyUpdateAvailable()
        break
      case 'UPDATE_COMPLETE':
        this.notifyUpdateComplete()
        break
      case 'SKIP_WAITING_RECEIVED':
        break
    }
  },

  notifyUpdateAvailable(newWorker = null) {
    this.updateCallbacks.forEach(callback => {
      callback({ type: 'UPDATE_AVAILABLE', newWorker })
    })
  },

  notifyUpdateComplete() {
    this.updateCallbacks.forEach(callback => {
      callback({ type: 'UPDATE_COMPLETE' })
    })
  },

  onUpdateEvent(callback) {
    this.updateCallbacks.add(callback)
    return () => this.updateCallbacks.delete(callback)
  },

  triggerUpdate() {
    if (!this.serviceWorkerRegistration) return Promise.resolve(false)

    return new Promise((resolve) => {
      const messageChannel = new MessageChannel()
      
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data?.type === 'SKIP_WAITING_RECEIVED')
      }

      setTimeout(() => resolve(false), 5000)

      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(
          { type: 'SKIP_WAITING' },
          [messageChannel.port2]
        )
      } else {
        resolve(false)
      }
    }).catch(error => {
      console.error('Update trigger failed:', error)
      return false
    })
  },

  handleVisibilityChange() {
    if (document.hidden) return

    authStorageService.isAuthenticated()
      .then(isAuthenticated => {
        if (!isAuthenticated) return

        const now = Date.now()
        if (now - this.lastFetchTimestamp > REFETCH_INTERVAL) {
          if (this.store) {
            this.store.dispatch(fetchForms(true))
            this.lastFetchTimestamp = now
          }
        }
      })
  },

  installPWA() {
    if (!this.deferredPrompt) {
      return Promise.resolve(false)
    }

    return this.deferredPrompt.prompt()
      .then(() => this.deferredPrompt.userChoice)
      .then(() => {
        this.deferredPrompt = null
        return true
      })
      .catch(() => false)
  },

  showInstallPromotion() {},
  hideInstallPromotion() {},

  isInstallable() {
    return this.deferredPrompt !== null
  },

  isInstalled() {
    return window.matchMedia('(display-mode: standalone)').matches
  }
}